import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AnimatedText from '../components/AnimatedText';
import '../styles/ToolPage.css';
import Navbar from './Navbar';
import { saveProfile, addHistory } from '../utils/storage';
import { useTranslation } from 'react-i18next';
import { translateText } from '../utils/translateText';

function ToolPage() {
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('');
  const [zodiac, setZodiac] = useState('');
  const [showError, setShowError] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [translated, setTranslated] = useState({
    heyThere: t("hey_there"),
    enterWeight: t("enter_weight"),
    enterHeight: t("enter_height"),
    enterAge: t("enter_age"),
    selectZodiac: t("select_zodiac"),
    letsGo: t("lets_go"),
    errorTitle: t("error_title"),
    retryBtn: t("retry_btn")
  });

  useEffect(() => {
    const lang = i18n.language;
    const translateAll = async () => {
      if (lang !== 'en') {
        const heyThere = await translateText("Hey there! ❣️.", lang);
        const enterWeight = await translateText("Enter your weight (kg)", lang);
        const enterHeight = await translateText("Enter your height (cm)", lang);
        const enterAge = await translateText("Enter your age", lang);
        const selectZodiac = await translateText("Select your zodiac", lang);
        const letsGo = await translateText("Let's Go", lang);
        const errorTitle = await translateText("Please fill all the fields correctly!", lang);
        const retryBtn = await translateText("Retry", lang);

        setTranslated({
          heyThere,
          enterWeight,
          enterHeight,
          enterAge,
          selectZodiac,
          letsGo,
          errorTitle,
          retryBtn
        });
      } else {
        setTranslated({
          heyThere: "Hey there! ❣️",
          enterWeight: "Enter your weight (kg)",
          enterHeight: "Enter your height (cm)",
          enterAge: "Enter your age",
          selectZodiac: "Select your zodiac",
          letsGo: "Let's Go",
          errorTitle: "Please fill all the fields correctly!",
          retryBtn: "Retry"
        });
      }
    };

    translateAll();
  }, [i18n.language]);

  const handleSubmit = () => {
    const w = Number(weight);
    const h = Number(height);
    const a = Number(age);

    if (!w || !h || !a || w < 20 || w > 350 || h < 80 || h > 250 || a < 13 || a > 120) {
      setShowError(true);
      return;
    }

    const bmi = w / ((h / 100) ** 2);
    const resultData = {
      bmi: bmi.toFixed(1),
      weight: w,
      height: h,
      age: a,
      gender,
      zodiac,
      error: false,
      createdAt: new Date().toISOString(),
    };

    try {
      localStorage.setItem('userHeight', String(h));
      localStorage.setItem('userWeight', String(w));
      localStorage.setItem('lastBMI', resultData.bmi);
      localStorage.setItem('userZodiac', zodiac);
      saveProfile({ weight: w, height: h, age: a, gender, zodiac });
      addHistory(resultData);
      window.dispatchEvent(new Event('profile-updated'));
    } catch {}

    navigate('/result', { state: resultData });
  };

  return (
    <>
      <Navbar />

      <div className="top-bar">
        <div style={{ marginLeft: "auto" }}></div>
      </div>

      <div className="container page">
        <section className="section form-section">
          <div className="tool-kicker">{t("mvp.form_kicker")}</div>
          <AnimatedText text={t("mvp.form_title")} className="tool-animated-text" />
          <p className="tool-intro">{t("mvp.form_intro")}</p>

          <div className="form-box">
            <label><span>{t("mvp.age")}</span><input aria-label={t("mvp.age")} type="number" min="13" max="120" placeholder={t("mvp.age_example")} value={age} onChange={(e) => setAge(e.target.value)} /></label>
            <label><span>{t("mvp.height")} <small>{t("cm")}</small></span><input aria-label={t("mvp.height")} type="number" min="80" max="250" placeholder={t("mvp.height_example")} value={height} onChange={(e) => setHeight(e.target.value)} /></label>
            <label><span>{t("mvp.weight")} <small>{t("kg")}</small></span><input aria-label={t("mvp.weight")} type="number" min="20" max="350" placeholder={t("mvp.weight_example")} value={weight} onChange={(e) => setWeight(e.target.value)} /></label>

            <details className="optional-details"><summary>{t("mvp.optional")}</summary><div className="optional-fields">

            <select
              value={zodiac}
              onChange={(e) => setZodiac(e.target.value)}
              className="form-input"
            >
              <option value="">{translated.selectZodiac}</option>
              <option value="aries">Aries</option>
              <option value="taurus">Taurus</option>
              <option value="gemini">Gemini</option>
              <option value="cancer">Cancer</option>
              <option value="leo">Leo</option>
              <option value="virgo">Virgo</option>
              <option value="libra">Libra</option>
              <option value="scorpio">Scorpio</option>
              <option value="sagittarius">Sagittarius</option>
              <option value="capricorn">Capricorn</option>
              <option value="aquarius">Aquarius</option>
              <option value="pisces">Pisces</option>
            </select>

            <div className="gender-group" role="radiogroup" aria-label={t("select_gender")}>
              {['male', 'female'].map((opt) => (
                <label
                  key={opt}
                  className={`gender-chip ${gender === opt ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="gender"
                    value={opt}
                    checked={gender === opt}
                    onChange={(e) => setGender(e.target.value)}
                  />
                  <span className="label-text">{t(opt)}</span>
                </label>
              ))}
            </div>
            </div></details>
            <p className="privacy-note"><span>✓</span> {t("mvp.saved_device")}</p>
          </div>

          <div className="lets-go-wrapper">
            <button className="lets-go-btn" onClick={handleSubmit}>
              {translated.letsGo}
            </button>
          </div>
        </section>

        {showError && (
          <div className="error-overlay">
            <div className="error-popup">
              <h2>{translated.errorTitle}</h2>
              <button className="retry-btn" onClick={() => setShowError(false)}>
                {translated.retryBtn}
              </button>
            </div>
          </div>
        )}

        <footer className="footer">© All rights reserved – Ranjith Gopinath</footer>
      </div>
    </>
  );
}

export default ToolPage;
